import { deepMemoize } from '@winter-love/utils';

const createMediaQuery = (size) => `@media screen and (min-width: ${size})`;

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

/* eslint-disable max-params */
const rawGetScale = (theme, key, def, props, undef) => {
    const _key = (key && key.split) ? key.split('.') : [key];
    let _object = theme;
    for (const element of _key) {
        _object = _object ? _object[element !== null && element !== void 0 ? element : ''] : undef;
    }
    return _object === undef ? def : _object;
};
const getScale = deepMemoize({ maxSize: 100 })(rawGetScale);

const mergeStyle = (first, second) => {
    return Object.keys(first).reduce((result, key) => {
        if (!first[key] || typeof second[key] !== 'object') {
            return result;
        }
        result[key] = Object.assign(Object.assign({}, first[key]), second[key]);
        return result;
    }, Object.assign(Object.assign({}, first), second));
};

const parseResponsiveObject = (breakpoints, sx, scale, raw, _props) => {
    return Object.keys(raw).reduce((result, key) => {
        const value = raw[key];
        const breakpoint = breakpoints[key];
        const style = sx(value, scale, _props, key);
        if (breakpoint) {
            const media = createMediaQuery(breakpoint);
            result[media] = Object.assign(Object.assign({}, result[media]), style);
            return result;
        }
        Object.assign(result, style);
        return result;
    }, {});
};

const parseResponsiveStyle = (mediaQueries, sx, scale, raw, _props) => {
    return raw.slice(0, mediaQueries.length).reduce((result, value, index) => {
        const media = mediaQueries[index];
        const style = sx(value, scale, _props, index);
        if (media) {
            result[media] = Object.assign(Object.assign({}, result[media]), style);
            return result;
        }
        Object.assign(result, style);
        return result;
    }, {});
};

// sort object-value responsive styles
const sortStyle = (object) => {
    return Object.keys(object)
        .sort((first, second) => first.localeCompare(second, undefined, {
        numeric: true,
        sensitivity: 'base',
    }))
        .reduce((result, key) => {
        result[key] = object[key];
        return result;
    }, {});
};

const SM_BREAKPOINT = 40;
const MD_BREAKPOINT = 52;
const LG_BREAKPOINT = 64;
const DEFAULT_BREAK_POINTS = [SM_BREAKPOINT, MD_BREAKPOINT, LG_BREAKPOINT];
const defaults = {
    breakpoints: DEFAULT_BREAK_POINTS.map((size) => `${size}em`),
};
const createParser = (config) => {
    const cache = {};
    const parse = (props) => {
        // let styles = {}
        let shouldSort = false;
        const isCacheDisabled = props.theme && props.theme.disableStyledSystemCache;
        const { theme } = props, rest = __rest(props, ["theme"]);
        const { __propStylePassThrough__ = false } = theme !== null && theme !== void 0 ? theme : {};
        const styles = Object.keys(rest).reduce((result, key) => {
            const raw = props[key];
            if (!config[key]) {
                if (__propStylePassThrough__) {
                    return Object.assign(result, { [key]: raw });
                }
                return result;
            }
            if (typeof raw === 'undefined') {
                return result;
            }
            const sx = config[key];
            const scale = getScale(theme, sx.scale, sx.defaults);
            if (typeof raw === 'object') {
                cache.breakpoints =
                    (!isCacheDisabled && cache.breakpoints) ||
                        getScale(theme, 'breakpoints', defaults.breakpoints);
                if (Array.isArray(raw)) {
                    cache.media = (!isCacheDisabled && cache.media) || [
                        // eslint-disable-next-line unicorn/no-null
                        null,
                        // eslint-disable-next-line unicorn/no-array-callback-reference
                        ...cache.breakpoints.map((size) => createMediaQuery(size)),
                    ];
                    return mergeStyle(result, parseResponsiveStyle(cache.media, sx, scale, raw, props));
                }
                if (raw !== null) {
                    shouldSort = true;
                    return mergeStyle(result, parseResponsiveObject(cache.breakpoints, sx, scale, raw, props));
                }
                return result;
            }
            return Object.assign(result, sx(raw, scale, props));
        }, {});
        // sort object-based responsive styles
        if (shouldSort) {
            return sortStyle(styles);
        }
        return styles;
    };
    parse.config = config;
    parse.propNames = Object.keys(config);
    parse.cache = cache;
    const keys = Object.keys(config).filter((key) => key !== 'config');
    if (keys.length > 1) {
        const parses = keys.reduce((result, key) => {
            result[key] = createParser({ [key]: config[key] });
            return result;
        }, {});
        return Object.assign(parse, parses);
    }
    return parse;
};

const getValue = (key, scale) => getScale(scale, key, key);
const createStyleFunction = (options) => {
    const { defaultScale, properties, property, scale, transform = getValue, } = options;
    const _properties = properties || [property];
    const sx = (value, scale, _props) => {
        const style = transform(value, scale, _props);
        if (style === null) {
            return;
        }
        return _properties.reduce((result, prop) => {
            if (prop) {
                result[prop] = style;
            }
            return result;
        }, {});
    };
    sx.scale = scale;
    sx.defaults = defaultScale;
    return sx;
};
const fillProperty = (option, key) => {
    const { property, properties } = option, rest = __rest(option, ["property", "properties"]);
    let _properties;
    if (property) {
        _properties = [property];
    }
    else if (properties) {
        _properties = [...properties];
    }
    else {
        _properties = [key];
    }
    return Object.assign(Object.assign({}, rest), { properties: _properties });
};
const createSystemConfig = (config) => (configNext = {}) => {
    return Object.assign(Object.assign({}, config), configNext);
};
const system = (options = {}) => {
    const _options = Object.assign({}, options);
    const config = Object.keys(_options).reduce((config, key) => {
        const option = options[key];
        if (option === true) {
            config[key] = createStyleFunction({
                property: key,
                scale: key,
            });
            return config;
        }
        if (typeof option === 'function') {
            config[key] = option;
            return config;
        }
        config[key] = createStyleFunction(fillProperty(option, key));
        return config;
    }, {});
    return createParser(config);
};

const compose = (...parsers) => {
    const config = parsers.reduce((result, parser) => {
        if (parser && parser.config) {
            return Object.assign(result, parser.config);
        }
        return result;
    }, {});
    return createParser(config);
};

const deepParse = (style, parse, theme) => {
    const { deeps, left } = Object.keys(style).reduce((result, key) => {
        const value = style[key];
        if (typeof value === 'object' && !Array.isArray(value)) {
            result.deeps[key] = deepParse(value, parse, theme);
            return result;
        }
        result.left[key] = value;
        return result;
    }, { deeps: {}, left: {} });
    return Object.assign(Object.assign({}, parse(Object.assign(Object.assign({}, left), { theme }))), deeps);
};
const createVariant = (parse) => (options) => {
    const { prop, scale, variants } = options;
    return system({
        [prop]: Object.assign((value, scale, props) => {
            const { theme } = props;
            const style = scale === null || scale === void 0 ? void 0 : scale[value];
            return typeof style === 'object' ? deepParse(style, parse, theme) : {};
        }, {
            defaults: variants,
            scale,
        }),
    });
};

export { DEFAULT_BREAK_POINTS, LG_BREAKPOINT, MD_BREAKPOINT, SM_BREAKPOINT, compose, createMediaQuery, createParser, createStyleFunction, createSystemConfig, createVariant, defaults, getScale, mergeStyle, parseResponsiveObject, parseResponsiveStyle, rawGetScale, sortStyle, system };
